import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { MenuItem } from '@mui/material'
import React, { useCallback, useMemo, useState } from 'react'
import { BannerKey } from 'src/Libs/banners/types'
import { useBannerContext } from '../provider/banner-context'
import useHideBanner from './use-hide-banner'
import { BANNER_SESSION_STORAGE_PREFIX } from '../constants'

interface ActionButtonHook {
  actions: React.ReactElement[]
  anchorEl: null | HTMLElement
  handleClick: (event: React.MouseEvent<HTMLElement>) => void
  closeMenu: () => void
}

/**
 * This hook handles logic to remove banners permanently or temporarily
 * @returns actions, anchorEl, handleClick and closeMenus to be parsed to ActionMenuButton
 */
const useActionButton = (bannerKey: BannerKey, onRemoveSuccessMessageKey: string | undefined): ActionButtonHook => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const { t } = useCustomTranslation()
  const { enqueueSuccessStack } = useSnackbar()
  const { hideBannerPermanently } = useHideBanner(onRemoveSuccessMessageKey)
  const { removeBanner } = useBannerContext()

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const closeMenu = (): void => {
    setAnchorEl(null)
  }

  const remindUser = useCallback((bannerKey: BannerKey) => {
    sessionStorage.setItem(`${BANNER_SESSION_STORAGE_PREFIX}${bannerKey}`, 'true')
    if (onRemoveSuccessMessageKey) enqueueSuccessStack(t(onRemoveSuccessMessageKey))
    removeBanner(bannerKey)
    closeMenu()
  }, []) // eslint-disable-line

  const dontRemindUser = useCallback(
    (bannerKey: BannerKey) => {
      hideBannerPermanently(bannerKey)
      closeMenu()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const actions = useMemo(() => {
    const remindAction = (
      <MenuItem key={0} onClick={() => remindUser(bannerKey)}>
        {t('installation-extension.banner.remind-me')}
      </MenuItem>
    )
    const dontRemindAction = (
      <MenuItem key={1} onClick={() => dontRemindUser(bannerKey)}>
        {t('installation-extension.banner.dont-remind-me')}
      </MenuItem>
    )
    return [remindAction, dontRemindAction]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t])

  return { actions, anchorEl, closeMenu, handleClick }
}

export default useActionButton
